import {
  PDFDocument,
  rgb,
  PDFString,
  PDFName,
  PDFArray,
  setCharacterSpacing,
  breakTextIntoLines
} from '@pdfme/pdf-lib';
import fontkit from 'pdf-fontkit';
import * as constants from '../data/constants';

class Pdf {
  pdfDoc;
  template;
  t;
  persona = '';
  fonts = {
    'Gotham': {
      regular: 'gotham/regular.otf',
      medium: 'gotham/medium.otf',
      bold: 'gotham/bold.otf',
    },
    'Noto-Sans': {
      regular: 'noto_sans/en/regular.ttf',
      medium: 'noto_sans/en/medium.ttf',
      bold: 'noto_sans/en/bold.ttf',
    },
    'Noto-Sans-JP': {
      regular: 'noto_sans/jp/regular.ttf',
      medium: 'noto_sans/jp/medium.ttf',
      bold: 'noto_sans/jp/bold.ttf',
    },
    'Noto-Sans-Thai': {
      regular: 'noto_sans/thai/regular.ttf',
      medium: 'noto_sans/thai/medium.ttf',
      bold: 'noto_sans/thai/bold.ttf',
    },
    'Noto-Sans-TC': {
      regular: 'noto_sans/tc/regular.ttf',
      medium: 'noto_sans/tc/medium.ttf',
      bold: 'noto_sans/tc/bold.ttf',
    },
    'Arial-Unicode': {
      regular: 'arial/regular.ttf',
      medium: 'arial/medium.ttf',
      bold: 'arial/bold.ttf',
    },
  };
  fontToLoad = null;
  font = {
    regular: null,
    medium: null,
    bold: null,
  };
  images = [];
  pageRef = [];
  answers = [];
  json = {};
  colours = {
    black: rgb(0, 0, 0),
    'light-grey': rgb(241 / 255, 225 / 255, 237 / 255),
    grey: rgb(78 / 255, 68 / 255, 78 / 255),
    'dark-grey': rgb(30 / 255, 0, 19 / 255),
    white: rgb(1, 1, 1),
    'light-purple': rgb(217 / 255, 193 / 255, 216 / 255),
    purple: rgb(122 / 255, 18 / 255, 107 / 255),
    'deep-purple': rgb(77 / 255, 20 / 255, 74 / 255),
  };
  wordBreaks = [' ', '、', '。', '：', '一', '，'];

  constructor() {
    if (undefined === window['pdf']) {
      this.json = window.translation_json;
    } else {
      this.json = window['pdf'];
    }

    this.fontToLoad = this.fonts[this.json['tool_ui']['global']['font']];
  }

  setAnswers(answers) {
    this.persona = answers.q13 + '_' + answers.q14;
    this.answers = answers;
  }

  setTranslation(translation) {
    this.t = translation;
  }

  loadFont(name, weight, preloadOnly = false) {
    return new Promise((resolve, reject) => {
      fetch(process.env.REACT_APP_FONTS_PATH + name)
        .then((res) => {
          res.arrayBuffer().then((arrayBuffer) => {
            if (!preloadOnly) {
              this.pdfDoc.embedFont(arrayBuffer).then((font) => {
                this.font[weight] = font;
                resolve();
              }).catch((e) => {
                this.loadFont('Gotham', weight).then(() => {
                  resolve();
                });
              });
            }
          });
        })
        .catch((e) => reject(e));
    });
  }

  loadImage(imgKey) {
    return new Promise((resolve, reject) => {
      const imgUrl = this.t(imgKey);
      const imageParts = imgUrl.split('.');
      const ext = imageParts[imageParts.length - 1];

      fetch(imgUrl).then((res) => {
        res
          .arrayBuffer()
          .then((arrayBuffer) => {
            if (['png'].includes(ext)) {
              try {
                this.pdfDoc.embedPng(arrayBuffer).then((image) => {
                  this.images[imgKey] = image;

                  resolve();
                }).catch((e) => {
                  console.log('image failed to load:', imgKey, e);
                });
              } catch (e) {
                console.log('image failed to load:', imgKey, e);
              }
            } else {
              try {
                this.pdfDoc.embedJpg(arrayBuffer).then((image) => {
                  this.images[imgKey] = image;

                  resolve();
                }).catch((e) => {
                  console.log('image failed to load:', imgKey, e);
                });
              } catch (e) {
                console.log('image failed to load:', imgKey, e);
              }
            }
          })
          .catch((e) => reject(e));
      });
    });
  }

  getBaseTemplate() {
    return new Promise((resolve, reject) => {
      fetch(process.env.REACT_APP_PDF_TEMPLATE)
        .then((res) => resolve(res.arrayBuffer()))
        .catch((e) => reject(e));
    });
  }

  addLink(page, uri, rect) {
    const ref = this.pdfDoc.context.register(
      this.pdfDoc.context.obj({
        Type: 'Annot',
        Subtype: 'Link',
        Rect: rect,
        Border: [0, 0, 0],
        backgroundColor: this.colours['white'],
        C: [0, 0, 0],
        A: {
          Type: 'Action',
          S: 'URI',
          URI: PDFString.of(uri),
        },
      })
    );

    const annots = page.node.lookup(PDFName.of('Annots'), PDFArray);
    annots.push(ref);

    page.node.set(PDFName.of('Annots'), this.pdfDoc.context.obj(annots));
  }

  loadAllFonts(preloadOnly = false) {
    return [
      this.loadFont(this.fontToLoad ? this.fontToLoad.regular : this.fonts['Gotham'].regular, 'regular', preloadOnly),
      this.loadFont(this.fontToLoad ? this.fontToLoad.medium : this.fonts['Gotham'].medium, 'medium', preloadOnly),
      this.loadFont(this.fontToLoad ? this.fontToLoad.bold : this.fonts['Gotham'].bold, 'bold', preloadOnly),
    ];
  }

  loadImagesForPage(images) {
    return new Promise((resolve, reject) => {
      Promise.all(images).then(() => {
        resolve();
      });
    });
  }

  setup() {
    return new Promise((resolve, reject) => {
      this.getBaseTemplate()
        .then((template) => {
          // Load our PDF template
          PDFDocument.load(template).then((pdfTemplate) => {
            this.template = pdfTemplate;

            // Convert pages into easy to read key types
            this.pageRef = {
              cover: 0,
              service: 1,
              'service-2': 2,
              devices: 3,
              'smart-collaboration': 4,
            };

            // Create our main PDF
            PDFDocument.create().then((pdfDoc) => {
              this.pdfDoc = pdfDoc;
              this.pdfDoc.registerFontkit(fontkit);

              Promise.all(this.loadAllFonts()).then(() => {
                resolve();
              });
            });
          });
        })
        .catch((e) => reject(e));
    });
  }

  linesOfTextAtSize(options) {
    const lines = breakTextIntoLines(
      options.text,
      this.wordBreaks,
      options.maxWidth,
      (t) => options.font.widthOfTextAtSize(t, options.fontSize)
    );

    return lines.length;
  }

  getTemplate() {
    return this.template;
  }

  getPageFromTemplate(name) {
    return new Promise((resolve, reject) => {
      this.pdfDoc
        .copyPages(this.getTemplate(), [this.pageRef[name]])
        .then((clone) => {
          resolve(clone);
        })
        .catch((e) => reject(e));
    });
  }

  getLastPageAdded() {
    // Get all pages
    const pages = this.pdfDoc.getPages();
    pages[pages.length - 1].pushOperators(setCharacterSpacing(1));

    return pages[pages.length - 1];
  }

  drawTable(page, overrideOptions, data, key) {
    let tableHeight = 0;
    let options = {
      columnWidth: overrideOptions.columnWidth ?? 200,
      params: {
        x: overrideOptions.params?.x ?? 40,
        y: overrideOptions.params?.y ?? 40,
        colWidths: overrideOptions.params?.colWidths ?? [],
      },
      headingStyle: {
        paddingX: overrideOptions.headingStyle?.paddingX ?? 2,
        paddingY: overrideOptions.headingStyle?.paddingY ?? 2,
        fontSize: overrideOptions.headingStyle?.fontSize ?? 12,
        textColor:
          overrideOptions.headingStyle?.textColor ?? this.colours['white'],
        cellColor:
          overrideOptions.headingStyle?.cellColor ?? this.colours['black'],
        borderColor:
          overrideOptions.headingStyle?.borderColor ?? this.colours['black'],
        font: overrideOptions.headingStyle?.font ?? this.font.regular,
        align: overrideOptions.headingStyle?.align ?? 'left',
        cellHeight: overrideOptions.headingStyle?.cellHeight ?? null,
      },
      rowStyle: {
        paddingX: overrideOptions.rowStyle?.paddingX ?? 2,
        paddingY: overrideOptions.rowStyle?.paddingY ?? 2,
        fontSize: overrideOptions.rowStyle?.fontSize ?? 12,
        textColor: overrideOptions.rowStyle?.textColor ?? this.colours['black'],
        cellColor: overrideOptions.rowStyle?.cellColor ?? this.colours['white'],
        borderColor:
          overrideOptions.rowStyle?.borderColor ?? this.colours['black'],
        font: overrideOptions.rowStyle?.font ?? this.font.regular,
        align: overrideOptions.rowStyle?.align ?? 'left',
        cellHeight: overrideOptions.rowStyle?.cellHeight ?? null,
      },
      firstColumnStyle: {
        paddingX:
          overrideOptions.firstColumnStyle?.paddingX ??
          overrideOptions.rowStyle?.paddingX ??
          2,
        paddingY:
          overrideOptions.firstColumnStyle?.paddingY ??
          overrideOptions.rowStyle?.paddingY ??
          2,
        fontSize:
          overrideOptions.firstColumnStyle?.fontSize ??
          overrideOptions.rowStyle?.fontSize ??
          12,
        textColor:
          overrideOptions.firstColumnStyle?.textColor ??
          overrideOptions.rowStyle?.textColor ??
          this.colours['black'],
        cellColor:
          overrideOptions.firstColumnStyle?.cellColor ??
          overrideOptions.rowStyle?.cellColor ??
          this.colours['white'],
        borderColor:
          overrideOptions.firstColumnStyle?.borderColor ??
          overrideOptions.rowStyle?.borderColor ??
          this.colours['black'],
        font:
          overrideOptions.firstColumnStyle?.font ??
          overrideOptions.rowStyle?.font ??
          this.font.regular,
        align:
          overrideOptions.firstColumnStyle?.align ??
          overrideOptions.rowStyle?.align ??
          'left',
      },
    };

    if (null === options.headingStyle.cellHeight) {
      options.headingStyle.cellHeight =
        options.headingStyle.font.heightAtSize(options.headingStyle.fontSize) +
        options.headingStyle.paddingY * 2;
    }

    if (null === options.rowStyle.cellHeight) {
      options.rowStyle.cellHeight =
        options.rowStyle.font.heightAtSize(options.rowStyle.fontSize) +
        options.rowStyle.paddingY * 2;
    }

    let colX = options.params.x;
    let colY = options.params.y;
    if (data.columns) {
      data.columns.forEach((item, index) => {
        if (undefined === options.params.colWidths[index]) {
          options.params.colWidths.push(options.columnWidth);
        }
        if (null !== (item?.[`${key}_name`] || item)) {
          const paragraph = this.addParagraphs(
            page,
            item?.[`${key}_name`] || item,
            {
              x:
                colX +
                ('center' === options.headingStyle.align
                  ? options.params.colWidths[index] / 2 -
                  options.headingStyle.font.widthOfTextAtSize(
                    item?.[`${key}_name`] || item,
                    options.headingStyle.fontSize ?? 12
                  ) /
                  2
                  : options.headingStyle.paddingX),
              y: colY + options.headingStyle.paddingY + 3,
              fontSize: options.headingStyle.fontSize ?? 12,
              lineHeight: options.headingStyle.fontSize ?? 12,
              lineGap: 0.5,
              maxWidth:
                (options.params.colWidths[index] ?? options.columnWidth) -
                options.headingStyle.paddingX * 2 -
                3,
              font: options.headingStyle.font,
              color: options.headingStyle.textColor ?? this.colours['white'],
              calcHeightOnly: true,
            }
          );

          if (paragraph.height > options.headingStyle.cellHeight) {
            options.headingStyle.cellHeight = paragraph.height + options.headingStyle.paddingY;
          }
        }
      });
      data.columns.forEach((item, index) => {
        if (null !== (item?.[`${key}_name`] || item)) {
          page.drawRectangle({
            x: colX,
            y: colY,
            width: options.params.colWidths[index] ?? options.columnWidth,
            height: options.headingStyle.cellHeight,
            color: options.headingStyle.cellColor ?? this.colours['black'],
            borderWidth: options.headingStyle.borderWidth ?? 0.5,
            borderColor: options.headingStyle.borderColor ?? this.colours['black'],
          });

          this.addParagraphs(page, item?.[`${key}_name`] || item, {
            x:
              colX +
              ('center' === options.headingStyle.align
                ? options.params.colWidths[index] / 2 -
                  options.headingStyle.font.widthOfTextAtSize(
                    item?.[`${key}_name`] || item,
                    options.headingStyle.fontSize ?? 12
                  ) /
                    2
                : options.headingStyle.paddingX),
            y: colY + options.headingStyle.paddingY + 3,
            fontSize: options.headingStyle.fontSize ?? 12,
            lineHeight: options.headingStyle.fontSize ?? 12,
            lineGap: 0.5,
            maxWidth:
              (options.params.colWidths[index] ?? options.columnWidth) -
              options.headingStyle.paddingX * 2 -
              3,
            font: options.headingStyle.font,
            color: options.headingStyle.textColor ?? this.colours['white'],
          });
        }

        colX = colX + options.params.colWidths[index] ?? options.columnWidth;
      });

      tableHeight = tableHeight + options.headingStyle.cellHeight;
    }

    let rowY = options.params.y - options.headingStyle.cellHeight;
    data.rows.forEach((items) => {
      let adjustY = 0;
      let rowX = options.params.x;
      let rowHeight =
        options.rowStyle.cellHeight ??
        options.rowStyle.font.heightAtSize(options.rowStyle.fontSize ?? 12) +
          options.rowStyle.paddingY * 2;

      items.forEach((item, index) => {
        const cellStyle =
          options[0 === index ? 'firstColumnStyle' : 'rowStyle'];
        const paragraph = this.addParagraphs(
          page,
          item?.[`${key}_name`] || item,
          {
            x:
              rowX +
              ('center' === cellStyle.align
                ? options.params.colWidths[index] / 2 -
                  cellStyle.font.widthOfTextAtSize(
                    item?.[`${key}_name`] || item,
                    cellStyle.fontSize ?? 12
                  ) /
                    2
                : cellStyle.paddingX),
            y: rowY + cellStyle.paddingY + 3,
            fontSize: cellStyle.fontSize ?? 12,
            lineHeight: cellStyle.fontSize ?? 12,
            lineGap: 0.2,
            maxWidth:
              (options.params.colWidths[index] ?? options.columnWidth) -
              (cellStyle.paddingX * 2) - 2,
            font: cellStyle.font,
            color: cellStyle.textColor ?? this.colours['black'],
            calcHeightOnly: true,
          }
        );

        if (paragraph.height > rowHeight) {
          adjustY = parseInt(paragraph.height) - options.rowStyle.cellHeight + options.rowStyle.paddingY + 3;
          rowHeight = parseInt(paragraph.height) + options.rowStyle.paddingY + 3;
        }
      });

      items.forEach((item, index) => {
        const cellStyle =
          options[0 === index ? 'firstColumnStyle' : 'rowStyle'];

        page.drawRectangle({
          x: rowX,
          y: rowY - adjustY,
          width: options.params.colWidths[index] ?? options.columnWidth,
          height: rowHeight,
          color: cellStyle.cellColor ?? this.colours['white'],
          borderWidth: cellStyle.borderWidth ?? 0.5,
          borderColor: cellStyle.borderColor ?? this.colours['black'],
        });

        this.addParagraphs(page, item?.[`${key}_name`] || item, {
          x:
            rowX +
            ('center' === cellStyle.align
              ? options.params.colWidths[index] / 2 -
                cellStyle.font.widthOfTextAtSize(
                  item?.[`${key}_name`] || item,
                  cellStyle.fontSize ?? 12
                ) / 2
              : cellStyle.paddingX),
          y: rowY + cellStyle.paddingY + 3,
          fontSize: cellStyle.fontSize ?? 12,
          lineHeight: cellStyle.fontSize ?? 12,
          lineGap: 0.2,
          maxWidth:
            (options.params.colWidths[index] ?? options.columnWidth) -
            (cellStyle.paddingX * 2) - 2,
          font: cellStyle.font,
          color: cellStyle.textColor ?? this.colours['black'],
        });

        if (item?.[`${key}_url`]) {
          this.addLink(page, item[`${key}_url`], [
            rowX + 4, // start x
            Math.floor(rowY - adjustY) + 4, // start y
            rowX + (options.params.colWidths[index] ?? options.columnWidth) - 4, // end x
            Math.floor(rowY - adjustY + rowHeight) - 4, // end y
          ]);
        }

        rowX = rowX + options.params.colWidths[index] ?? options.columnWidth;
      });

      rowY = rowY - rowHeight + 1;
      tableHeight = tableHeight + rowHeight;
    });

    return {
      height: tableHeight,
    };
  }

  addParagraphs(page, text, options, split = '\\n') {
    const paragraphs = text.split(split);

    let totalHeight = 0;
    let startY = options.y;
    let calcHeightOnly = options.calcHeightOnly ?? false;

    for (let i = 0, l = paragraphs.length; i < l; i++) {
      page.pushOperators(setCharacterSpacing(options.charSpacing ?? -0.2));

      if (!calcHeightOnly) {
        page.drawText(paragraphs[i].trim(), {
          x: options.x,
          y: startY,
          size: options.fontSize,
          lineHeight: options.lineHeight,
          maxWidth: options.maxWidth,
          font: options.font,
          color: options.color,
          wordBreaks: this.wordBreaks,
        });
      }

      const lines = this.linesOfTextAtSize({
        text: paragraphs[i].trim(),
        fontSize: options.fontSize,
        maxWidth: options.maxWidth,
        font: options.font,
      });
      const lastHeight = (lines + options.lineGap) * options.font.heightAtSize(options.fontSize);

      startY = startY - lastHeight;

      totalHeight = totalHeight + lastHeight;
    }

    return {
      height: totalHeight,
    };
  }

  addCtaBanner(page, section) {
    page.drawImage(this.images[`tool_ui.cta_section.${section}_qr_code`], {
      x: 44,
      y: 21,
      width: 72,
      height: 72,
    });

    page.pushOperators(setCharacterSpacing(-0.2));

    this.addParagraphs(page, this.t(`tool_ui.cta_section.${section}_text_1`), {
      x: 154,
      y: 64,
      fontSize: 12,
      lineHeight: 12,
      lineGap: 0,
      maxWidth: 500,
      font: this.font.bold,
      color: this.colours['white'],
    });

    this.addParagraphs(page, this.t(`tool_ui.cta_section.${section}_text_2`), {
      x: 154,
      y: 46,
      fontSize: 12,
      lineHeight: 12,
      lineGap: 0,
      maxWidth: 500,
      font: this.font.bold,
      color: this.colours['white'],
    });

    this.addLink(page, this.t(`tool_ui.cta_section.${section}_url`), [
      0, // start x
      0, // start y
      596, // end x
      113, // end y
    ]);
  }

  addCoverPage(params) {
    return new Promise((pageDone) => {
      let images = [
        this.loadImage('tool_ui.cta_section.cover_qr_code'),
        this.loadImage('tool_ui.cover_page.check_list_icon'),
      ];

      for (let i = 1, l = 6; i <= l; i++) {
        images.push(
          this.loadImage(`tool_ui.cover_page.reason${i}_icon_img_url`)
        );
      }

      this.loadImagesForPage(images).then(() => {
        this.getPageFromTemplate('cover').then((coverPage) => {
          this.pdfDoc.addPage(coverPage[0]);

          const contents = params.contents;

          // Get last page added
          const page = this.getLastPageAdded();

          // page.pushOperators(setCharacterSpacing(-1.495));
          page.pushOperators(setCharacterSpacing(-4));
          const title = this.addParagraphs(page, this.t('tool_ui.cover_page.page_title'), {
            x: 44,
            y: 707,
            fontSize: 30,
            // lineHeight: 32.3,
            lineHeight: 30,
            lineGap: 0,
            maxWidth: 510,
            font: this.font.bold,
            color: this.colours['white'],
          });

          page.pushOperators(setCharacterSpacing(-0.2));
          const subTitle = this.addParagraphs(page, this.t('tool_ui.cover_page.page_subtitle'), {
            x: 44,
            y: 723 - (title.height),
            fontSize: 16,
            lineHeight: 16,
            lineGap: 0,
            maxWidth: 510,
            font: this.font.bold,
            color: this.colours['white'],
          });
          this.addParagraphs(page, this.t('tool_ui.cover_page.page_body'), {
            x: 44,
            y: 723 - (title.height + subTitle.height),
            fontSize: 10,
            lineHeight: 13,
            lineGap: 0,
            maxWidth: 510,
            font: this.font.regular,
            color: this.colours['white'],
          });

          let contentStartX = 44;
          for (let i = 0, l = contents.length - 1; i <= l; i++) {
            page.drawImage(this.images[`tool_ui.cover_page.check_list_icon`], {
              x: contentStartX,
              y: 511.5,
              width: 17,
              height: 17,
            });

            this.addParagraphs(page, contents[i], {
              x: contentStartX + 21,
              y: 511.5 + 5,
              fontSize: 11,
              lineHeight: 12,
              lineGap: 0,
              maxWidth: 212,
              font: this.font.bold,
              color: this.colours['grey'],
            });

            contentStartX = contentStartX + 30 + this.font.bold.widthOfTextAtSize(contents[i], 11);
          }

          page.pushOperators(setCharacterSpacing(-0.25));
          const wlStat = this.addParagraphs(page, this.t('tool_ui.cover_page.wl_stat'), {
            x: 44,
            y: 478,
            fontSize: 14,
            lineHeight: 15,
            lineGap: 0,
            maxWidth: 540,
            font: this.font.bold,
            color: this.colours['purple'],
          });
          page.pushOperators(setCharacterSpacing(-0.2));
          const wlStatSubtitle = this.addParagraphs(page, this.t('tool_ui.cover_page.wl_subtitle'), {
            x: 44,
            y: 478 - (wlStat.height),
            fontSize: 10,
            lineHeight: 12,
            lineGap: 0,
            maxWidth: 460,
            font: this.font.regular,
            color: this.colours['black'],
          });

          let yPos = 478 - 40 - wlStat.height - wlStatSubtitle.height;
          let startY = yPos;
          let startX = 44;
          for (let i = 1, l = 6; i <= l; i++) {
            page.drawImage(
              this.images[`tool_ui.cover_page.reason${i}_icon_img_url`],
              {
                x: startX,
                y: startY,
                width: 32,
                height: 32,
              }
            );
            const reasonTitle = this.addParagraphs(page, this.t(`tool_ui.cover_page.reason${i}_title`), {
              x: startX + 44.5,
              y: startY + 24,
              fontSize: 10,
              lineHeight: 12,
              lineGap: 0,
              maxWidth: 212,
              font: this.font.bold,
              color: this.colours['deep-purple'],
            });
            this.addParagraphs(page, this.t(`tool_ui.cover_page.reason${i}_body`), {
              x: startX + 44.5,
              y: startY + 24 - (reasonTitle.height),
              fontSize: 9,
              lineHeight: 11,
              lineGap: 0,
              maxWidth: 212,
              font: this.font.regular,
              color: this.colours['black'],
            });

            const titleLines = this.linesOfTextAtSize({
              text: this.t(`tool_ui.cover_page.reason${i}_title`),
              fontSize: 10,
              maxWidth: 212,
              font: this.font.regular,
            });
            const bodyLines = this.linesOfTextAtSize({
              text: this.t(`tool_ui.cover_page.reason${i}_body`),
              fontSize: 9,
              maxWidth: 212,
              font: this.font.regular,
            });

            const lastHeight =
              titleLines * this.font.regular.heightAtSize(10) +
              bodyLines * this.font.regular.heightAtSize(9) +
              1.7 * this.font.regular.heightAtSize(10);
            startY = startY + 8 - lastHeight;

            if (3 === i) {
              startY = yPos;
              startX = startX + 265;
            }

            if (6 === i) {
              this.addParagraphs(page, this.t(`tool_ui.cover_page.ref_text`), {
                x: startX + 44.5,
                y: startY + 25,
                fontSize: 7,
                lineHeight: 10,
                lineGap: 0,
                maxWidth: 212,
                font: this.font.regular,
                color: this.colours['black'],
              });
            }
          }

          this.addCtaBanner(page, 'cover');

          pageDone();
        });
      });
    });
  }

  addServicePage(params) {
    return new Promise((pageDone) => {
      let images = [
        this.loadImage('tool_ui.cta_section.it_solutions_p1_qr_code'),
      ];

      // Service images
      params.items.forEach((item) => {
        images.push(this.loadImage(item.image));
      });

      this.loadImagesForPage(images).then(() => {
        this.getPageFromTemplate('service').then((servicePage) => {
          this.pdfDoc.addPage(servicePage[0]);

          const items = params.items;

          // Get last page added
          const page = this.getLastPageAdded();

          page.pushOperators(setCharacterSpacing(-1.495));
          const title = this.addParagraphs(page, this.t('tool_ui.solutions_p1.page_title'), {
            x: 43,
            y: 780,
            fontSize: 34,
            lineHeight: 32.3,
            lineGap: 0,
            maxWidth: 500,
            font: this.font.bold,
            color: this.colours['purple'],
          });

          page.pushOperators(setCharacterSpacing(-0.2));

          const subTitle = this.addParagraphs(
            page,
            this.t('tool_ui.solutions_p1.page_subtitle_1'),
            {
              x: 43,
              y: 795 - title.height,
              fontSize: 14,
              lineHeight: 14,
              maxWidth: 520,
              lineGap: 0,
              font: this.font.bold,
              color: this.colours['purple'],
            }
          );

          const body1 = this.addParagraphs(page, this.t(`tool_ui.solutions_p1.page_body_1`), {
            x: 43,
            y: 795 - (title.height + subTitle.height),
            fontSize: 10,
            lineHeight: 12,
            lineGap: 0,
            maxWidth: 520,
            font: this.font.regular,
            color: this.colours['black'],
          });

          const subTitle2 = this.addParagraphs(
            page,
            this.t('tool_ui.solutions_p1.page_subtitle_2'),
            {
              x: 43,
              y: 795 - 15 - (title.height + subTitle.height + body1.height),
              fontSize: 14,
              lineHeight: 14,
              lineGap: 0,
              maxWidth: 520,
              font: this.font.bold,
              color: this.colours['purple'],
            }
          );

          this.addParagraphs(page, this.t(`tool_ui.solutions_p1.page_body_2`), {
            x: 43,
            y: 795 - 15 - (title.height + subTitle.height + body1.height + subTitle2.height),
            fontSize: 10,
            lineHeight: 12,
            lineGap: 0,
            maxWidth: 520,
            font: this.font.regular,
            color: this.colours['black'],
          });

          this.addParagraphs(page, this.t(`tool_ui.solutions_p1.page_offering`), {
            x: 43,
            y: 529,
            fontSize: 14,
            lineHeight: 14,
            lineGap: 0,
            maxWidth: 430,
            font: this.font.bold,
            color: this.colours['purple'],
          });

          let yStart = 502;
          items.forEach((item) => {
            page.pushOperators(setCharacterSpacing(-0.2));

            page.drawImage(this.images[item.image], {
              x: 44,
              y: yStart - 102.5,
              width: 176,
              height: 111,
            });

            this.addParagraphs(page, item.solution_title, {
              x: 242,
              y: yStart,
              fontSize: 10,
              lineHeight: 12,
              lineGap: 0,
              maxWidth: 450,
              font: this.font.bold,
              color: this.colours['dark-grey'],
            });

            let yStartF = yStart - 14;
            let lastFeature = '';
            for (let i = 0, l = 3; i < l; i++) {
              if (item['solution_benefit_' + (i + 1)]) {
                if (lastFeature.length > 0) {
                  const lines = this.linesOfTextAtSize({
                    text: lastFeature,
                    fontSize: 9,
                    maxWidth: 318,
                    font: this.font.regular,
                  });

                  const lastHeight = (lines + 0.2) * this.font.regular.heightAtSize(10);

                  yStartF = yStartF - lastHeight;
                }

                page.drawCircle({
                  x: 244,
                  y: yStartF + 3,
                  size: 1.2,
                  color: this.colours['grey'],
                });

                this.addParagraphs(page, item['solution_benefit_' + (i + 1)].trim(), {
                  x: 253,
                  y: yStartF,
                  fontSize: 9,
                  lineHeight: 11,
                  lineGap: 0,
                  maxWidth: 318,
                  font: this.font.regular,
                  color: this.colours['grey'],
                });

                lastFeature = item['solution_benefit_' + (i + 1)].trim();
              }
            }

            yStart = yStart - 132.5;
          });

          this.addCtaBanner(page, 'it_solutions_p1');

          pageDone();
        });
      });
    });
  }

  addServicePage2(params) {
    return new Promise((pageDone) => {
      let images = [
        this.loadImage('tool_ui.cta_section.it_solutions_p2_qr_code'),
      ];

      params.items.forEach((item) => {
        images.push(this.loadImage(item.image));
      });

      this.loadImagesForPage(images).then(() => {
        this.getPageFromTemplate('service-2').then((servicePage2) => {
          this.pdfDoc.addPage(servicePage2[0]);

          const items = params.items;

          // Get last page added
          const page = this.getLastPageAdded();

          let yStart = 789.5;
          items.forEach((item) => {
            page.pushOperators(setCharacterSpacing(-0.2));

            page.drawImage(this.images[item.image], {
              x: 44,
              y: yStart - 102.5,
              width: 176,
              height: 111,
            });

            this.addParagraphs(page, item.solution_title, {
              x: 242,
              y: yStart,
              fontSize: 10,
              lineHeight: 12,
              lineGap: 0,
              maxWidth: 450,
              font: this.font.bold,
              color: this.colours['dark-grey'],
            });

            let yStartF = yStart - 14;
            let lastFeature = '';
            for (let i = 0, l = 3; i < l; i++) {
              if (item['solution_benefit_' + (i + 1)]) {
                if (lastFeature.length > 0) {
                  const lines = this.linesOfTextAtSize({
                    text: lastFeature,
                    fontSize: 9,
                    maxWidth: 318,
                    font: this.font.regular,
                  });

                  const lastHeight = (lines + 0.2) * this.font.regular.heightAtSize(10);

                  yStartF = yStartF - lastHeight;
                }

                page.drawCircle({
                  x: 244,
                  y: yStartF + 3,
                  size: 1.2,
                  color: this.colours['grey'],
                });

                this.addParagraphs(page, item['solution_benefit_' + (i + 1)].trim(), {
                  x: 253,
                  y: yStartF,
                  fontSize: 9,
                  lineHeight: 11,
                  lineGap: 0,
                  maxWidth: 318,
                  font: this.font.regular,
                  color: this.colours['grey'],
                });

                lastFeature = item['solution_benefit_' + (i + 1)].trim();
              }
            }

            yStart = yStart - 132.5;
          });

          this.addCtaBanner(page, 'it_solutions_p2');

          pageDone();
        });
      });
    });
  }

  addDevicePage(params) {
    return new Promise((pageDone) => {
      const devices = params.devices;
      const accessories = params.accessories;
      const software = params.software;
      const phones = params.phones;
      const moto_solutions = params.moto_solutions;

      let images = [this.loadImage('tool_ui.cta_section.devices_qr_code')];

      this.loadImagesForPage(images).then(() => {
        this.getPageFromTemplate('devices').then((devicePage) => {
          this.pdfDoc.addPage(devicePage[0]);

          // Get last page added
          const page = this.getLastPageAdded();

          page.pushOperators(setCharacterSpacing(-1.495));
          const title = this.addParagraphs(page, this.t('tool_ui.devices_page.page_title'), {
            x: 43,
            y: 771,
            fontSize: 34,
            lineHeight: 32.3,
            lineGap: 0,
            maxWidth: 530,
            font: this.font.bold,
            color: this.colours['purple'],
          });
          page.pushOperators(setCharacterSpacing(-0.2));
          const subTitle = this.addParagraphs(
            page,
            this.t('tool_ui.devices_page.page_subtitle'),
            {
              x: 44,
              y: 791 - title.height,
              fontSize: 10,
              lineHeight: 10,
              lineGap: 0,
              maxWidth: 530,
              font: this.font.regular,
              color: this.colours['dark-grey'],
            }
          );

          let startTableY = 771 - (title.height + subTitle.height);

          if (this.answers.q1.includes('devices')) {
            page.pushOperators(setCharacterSpacing(-0.2));
            const devicesTable = this.drawTable(
              page,
              {
                params: {
                  x: 44,
                  y: startTableY,
                  colWidths: [107, 134.5, 134.5, 134.5],
                },
                headingStyle: {
                  fontSize: 10,
                  paddingX: 7,
                  paddingY: 7,
                  align: 'center',
                  textColor: this.colours['white'],
                  cellColor: this.colours['deep-purple'],
                  borderColor: this.colours['light-grey'],
                  font: this.font.bold,
                },
                rowStyle: {
                  fontSize: 10,
                  paddingX: 7,
                  paddingY: 7,
                  align: 'left',
                  textColor: this.colours['dark-grey'],
                  cellColor: this.colours['light-grey'],
                  borderColor: this.colours['grey'],
                  font: this.font.medium,
                },
                firstColumnStyle: {
                  fontSize: 10,
                  paddingX: 7,
                  paddingY: 7,
                  align: 'left',
                  textColor: this.colours['dark-grey'],
                  cellColor: this.colours['light-purple'],
                  borderColor: this.colours['grey'],
                  font: this.font.bold,
                },
              },
              {
                columns: [
                  null,
                  this.t('tool_ui.devices_page.device_table_good'),
                  this.t('tool_ui.devices_page.device_table_better'),
                  this.t('tool_ui.devices_page.device_table_best'),
                ],
                rows: devices.map((device) => {
                  return [device.type, device.good, device.better, device.best];
                }),
              },
              'product'
            );

            startTableY = startTableY - devicesTable.height;

            if (this.answers.q16.includes('phones')) {
              const phonesTables = this.drawTable(
                page,
                {
                  params: {
                    x: 44,
                    y: startTableY + 22,
                    colWidths: [107, 134.5 * 3],
                  },
                  rowStyle: {
                    fontSize: 10,
                    lineHeight: 10,
                    paddingX: 7,
                    paddingY: 7,
                    align: 'left',
                    textColor: this.colours['dark-grey'],
                    cellColor: this.colours['light-grey'],
                    borderColor: this.colours['grey'],
                    font: this.font.medium,
                  },
                  firstColumnStyle: {
                    fontSize: 10,
                    paddingX: 7,
                    paddingY: 7,
                    align: 'left',
                    textColor: this.colours['dark-grey'],
                    cellColor: this.colours['light-purple'],
                    borderColor: this.colours['grey'],
                    font: this.font.bold,
                  },
                },
                {
                  rows: phones.map((phone) => {
                    return [this.t('tool_ui.devices_page.phone'), phone];
                  }),
                },
                'product'
              );

              startTableY = startTableY - phonesTables.height;
            }

            if (
              this.answers.q16.includes('notebooks') ||
              this.answers.q16.includes('desktops') ||
              this.answers.q16.includes('tablets') ||
              this.answers.q16.includes('monitors')
            ) {
              this.drawTable(
                page,
                {
                  params: {
                    x: 44,
                    y: startTableY - 20,
                    colWidths: [153],
                  },
                  headingStyle: {
                    fontSize: 10,
                    paddingX: 7,
                    paddingY: 7,
                    textColor: this.colours['white'],
                    cellColor: this.colours['deep-purple'],
                    borderColor: this.colours['deep-purple'],
                    font: this.font.bold,
                  },
                  rowStyle: {
                    fontSize: 10,
                    lineHeight: 10,
                    paddingX: 7,
                    paddingY: 7,
                    textColor: this.colours['dark-grey'],
                    cellColor: this.colours['light-grey'],
                    borderColor: this.colours['grey'],
                    font: this.font.medium,
                  },
                },
                {
                  columns: [this.t('tool_ui.devices_page.accessories_title')],
                  rows: accessories.map((accessories) => {
                    return [accessories];
                  }),
                },
                'accessory'
              );
            }
          }

          if ('yes' === this.answers.q17) {
            page.pushOperators(setCharacterSpacing(-0.2));

            const softwareX =
              44 + (this.answers.q1.includes('devices') ? 153 + 26 : 0);

            this.drawTable(
              page,
              {
                params: {
                  x: softwareX - 2.5,
                  y: startTableY - 20,
                  colWidths: [158],
                },
                headingStyle: {
                  fontSize: 10,
                  paddingX: 7,
                  paddingY: 7,
                  textColor: this.colours['white'],
                  cellColor: this.colours['deep-purple'],
                  borderColor: this.colours['deep-purple'],
                  font: this.font.bold,
                },
                rowStyle: {
                  fontSize: 10,
                  lineHeight: 10,
                  paddingX: 7,
                  paddingY: 7,
                  textColor: this.colours['dark-grey'],
                  cellColor: this.colours['light-grey'],
                  borderColor: this.colours['grey'],
                  font: this.font.medium,
                },
              },
              {
                columns: [this.t('tool_ui.devices_page.software_title')],
                rows: software.map((software) => {
                  return [software];
                }),
              },
              'software'
            );
          }

          if (this.answers.q16.includes('phones')) {
            page.pushOperators(setCharacterSpacing(-0.2));

            const motoX =
              44 +
              (this.answers.q1.includes('devices') ? 153 + 26 : 0) +
              ('yes' === this.answers.q17 ? 153 + 26 : 0);

            this.drawTable(
              page,
              {
                params: {
                  x: motoX,
                  y: startTableY - 20,
                  colWidths: [153],
                },
                headingStyle: {
                  fontSize: 10,
                  paddingX: 7,
                  paddingY: 7,
                  textColor: this.colours['white'],
                  cellColor: this.colours['deep-purple'],
                  borderColor: this.colours['deep-purple'],
                  font: this.font.bold,
                },
                rowStyle: {
                  fontSize: 10,
                  lineHeight: 10,
                  paddingX: 7,
                  paddingY: 7,
                  textColor: this.colours['dark-grey'],
                  cellColor: this.colours['light-grey'],
                  borderColor: this.colours['grey'],
                  font: this.font.medium,
                },
              },
              {
                columns: [this.t('tool_ui.devices_page.moto_title')],
                rows: moto_solutions.map((moto_solution) => {
                  return [moto_solution];
                }),
              },
              'solution'
            );
          }

          this.addCtaBanner(page, 'devices');

          pageDone();
        });
      });
    });
  }

  addSmartCollaborationPage() {
    return new Promise((pageDone) => {
      this.loadImagesForPage([
        this.loadImage(`smart_collaboration.${this.answers.q18}.room_image`),
        this.loadImage(`tool_ui.cta_section.smart_collab_qr_code`),
      ]).then(() => {
        this.getPageFromTemplate('smart-collaboration').then(
          (smartCollaborationPage) => {
            this.pdfDoc.addPage(smartCollaborationPage[0]);

            // Get last page added
            const page = this.getLastPageAdded();

            page.drawImage(
              this.images[
                'smart_collaboration.' + this.answers.q18 + '.room_image'
              ],
              {
                x: 0,
                y: 485,
                width: 596,
                height: 377,
              }
            );

            page.pushOperators(setCharacterSpacing(-1.495));
            const title = this.addParagraphs(page, this.t(`smart_collaboration.${this.answers.q18}.title`), {
              x: 44,
              y: 438.5,
              fontSize: 34,
              lineHeight: 34,
              lineGap: 0,
              maxWidth: 510,
              font: this.font.bold,
              color: this.colours['purple'],
            });

            page.pushOperators(setCharacterSpacing(-0.28));

            const subTitle = this.addParagraphs(page, this.t(`smart_collaboration.${this.answers.q18}.subtitle`), {
              x: 44,
              y: 455.5 - title.height,
              fontSize: 14,
              lineHeight: 14,
              lineGap: 0,
              maxWidth: 510,
              font: this.font.bold,
              color: this.colours['purple'],
            });

            page.pushOperators(setCharacterSpacing(-0.2));

            this.addParagraphs(page, this.t(`smart_collaboration.${this.answers.q18}.body_left`), {
              x: 44,
              y: 455.5 - title.height - subTitle.height,
              fontSize: 10,
              lineHeight: 12.5,
              maxWidth: 260,
              lineGap: 1.2,
              font: this.font.regular,
              color: this.colours['black'],
            });

            this.addParagraphs(page, this.t(`smart_collaboration.${this.answers.q18}.body_right`), {
              x: 305,
              y: 455.5 - title.height - subTitle.height,
              fontSize: 10,
              lineHeight: 12.5,
              maxWidth: 260,
              lineGap: 1.2,
              font: this.font.regular,
              color: this.colours['black'],
            });

            this.addCtaBanner(page, 'smart_collab');

            pageDone();
          }
        );
      });
    });
  }

  filterToPersona(items) {
    let newItems = [];

    items.forEach((item) => {
      if ('yes' === item['relevant_to_persona']) {
        newItems.push(item);
      }
    });

    return newItems;
  }

  filterToDeviceType(items, type) {
    let newItems = [];

    items.forEach((item) => {
      if (type === item.product_type) {
        newItems.push(item);
      }
    });

    return newItems;
  }

  processPageQueue(pages) {
    return new Promise((finishQueue) => {
      let i = 0;

      // Process queue item
      const processQueue = () => {
        const page = pages[i];

        // Call page method
        (page.params
          ? this[page.method](page.params)
          : this[page.method]()
        ).then(() => {
          i = i + 1;

          if (i < pages.length) {
            // Process next page in queue
            processQueue();
          } else {
            // All pages processed lets finish!
            finishQueue();
          }
        });
      };

      // Process first page in queue
      processQueue();
    });
  }

  process() {
    return new Promise((resolve, reject) => {
      this.setup()
        .then(() => {
          let contents = [];

          if (this.answers.q1.includes('solutions')) {
            contents.push(this.t('tool_ui.cover_page.check_list1'));
          }
          if (this.answers.q1.includes('devices')) {
            contents.push(this.t('tool_ui.cover_page.check_list2'));
          }
          if ('yes' === this.answers.q17) {
            contents.push(this.t('tool_ui.cover_page.check_list3'));
          }
          if (
            this.answers.q1.includes('devices') &&
            constants.NOT_APPLICABLE !== this.answers.q18
          ) {
            contents.push(this.t('tool_ui.cover_page.check_list4'));
          }

          let pageQueue = [
            {
              method: 'addCoverPage',
              params: {
                contents: contents,
              },
            },
          ];

          if (this.answers.q1.includes('solutions')) {
            this.json.it_solutions[this.answers.service_level.name].forEach(
              (service, index) => {
                service.image = `it_solutions.${this.answers.service_level.name}.${index}.solution_img_url`;
              }
            );

            const solutions = this.filterToPersona(
              this.json.it_solutions[this.answers.service_level.name]
            );

            pageQueue.push({
              method: 'addServicePage',
              params: {
                items: solutions.slice(0, 3),
              },
            });

            if (solutions.length > 3) {
              pageQueue.push({
                method: 'addServicePage2',
                params: {
                  items: solutions.slice(3, 8),
                },
              });
            }
          }

          if (this.answers.q1.includes('devices')) {
            this.json.accessories[this.persona].forEach((accessory, index) => {
              accessory.image = `accessories.${this.persona}.${index}.accessory_img_url`;
            });

            let devices = [];
            let deviceTypes = [
              {
                t: 'tool_ui.devices.q16_r1',
                key: 'notebooks',
                name: 'Notebook',
              },
              {
                t: 'tool_ui.devices.q16_r2',
                key: 'desktops',
                name: 'Desktop',
              },
              {
                t: 'tool_ui.devices.q16_r3',
                key: 'tablets',
                name: 'Tablet',
              },
              {
                t: 'tool_ui.devices.q16_r4',
                key: 'monitors',
                name: 'Monitor',
              },
            ];

            deviceTypes.forEach((deviceType) => {
              if (this.answers.q16.includes(deviceType.key)) {
                let device = {
                  type: this.t(deviceType.t),
                  good: '',
                  better: '',
                  best: '',
                };

                ['good', 'better', 'best'].forEach((key) => {
                  const product = this.filterToDeviceType(
                    this.json.devices[this.persona][key],
                    deviceType.name
                  );

                  if (product[0]) {
                    device[key] = product[0];
                  }
                });

                devices.push(device);
              }
            });

            pageQueue.push({
              method: 'addDevicePage',
              params: {
                devices: devices,
                phones: [this.json.phone[this.persona]],
                accessories: this.filterToPersona(
                  this.json.accessories[this.persona]
                ),
                software: this.filterToPersona(
                  this.json.software[this.persona]
                ),
                moto_solutions: this.filterToPersona(
                  this.json.moto_solutions[this.persona]
                ),
              },
            });
            if (constants.NOT_APPLICABLE !== this.answers.q18) {
              pageQueue.push({
                method: 'addSmartCollaborationPage',
              });
            }
          }

          this.processPageQueue(pageQueue).then(() => {
            resolve(this.pdfDoc.save());
          });
        })
        .catch((e) => reject(e));
    });
  }

  save(output) {
    window.open(
      URL.createObjectURL(new Blob([output], { type: 'application/pdf' }))
    );
  }
}

export default Pdf;
